import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Fuel, LayoutDashboard, ClipboardList, Users2, LogOut, Loader2, TrendingUp, Droplets, IndianRupee, Truck, Gauge, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_COLORS, STATUS_ORDER } from "@/lib/statuses";

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("dashboard");
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [pricing, setPricing] = useState(null);
    const [filter, setFilter] = useState("All");
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [s, o, c, p] = await Promise.all([
                api.get("/admin/stats"),
                api.get(`/admin/orders${filter !== "All" ? `?status_filter=${encodeURIComponent(filter)}` : ""}`),
                api.get("/admin/customers"),
                api.get("/admin/rate"),
            ]);
            setStats(s.data);
            setOrders(o.data.orders);
            setCustomers(c.data.customers);
            setPricing(p.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, [filter]); // eslint-disable-line

    const updateStatus = async (orderId, status) => {
        try {
            await api.patch(`/admin/orders/${orderId}/status`, { status });
            toast.success(`Order marked ${status}`);
            loadAll();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
        }
    };

    const saveRate = async (price, note) => {
        try {
            const { data } = await api.patch("/admin/rate", { price_per_litre: price, rate_note: note });
            setPricing(data);
            toast.success("Rate updated");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update rate");
        }
    };

    const doLogout = async () => { await logout(); navigate("/"); };

    return (
        <div className="min-h-screen flex bg-slate-100" data-testid="admin-page">
            {/* Sidebar */}
            <aside className="w-64 bg-slate-900 text-white flex flex-col shrink-0" data-testid="admin-sidebar">
                <div className="p-6 border-b border-slate-800">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-blue-700 flex items-center justify-center rounded-sm">
                            <Fuel className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="font-heading font-black text-white text-sm tracking-tight">VAISHALI FUELS</div>
                            <div className="text-[10px] font-mono-plex text-yellow-400 uppercase tracking-widest">Admin Console</div>
                        </div>
                    </Link>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    <NavBtn icon={LayoutDashboard} label="Dashboard" active={tab === "dashboard"} onClick={() => setTab("dashboard")} testid="admin-tab-dashboard" />
                    <NavBtn icon={ClipboardList} label="Orders" active={tab === "orders"} onClick={() => setTab("orders")} testid="admin-tab-orders" />
                    <NavBtn icon={Users2} label="Customers" active={tab === "customers"} onClick={() => setTab("customers")} testid="admin-tab-customers" />
                </nav>
                <div className="p-4 border-t border-slate-800">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-plex">Signed in as</div>
                    <div className="text-sm text-white truncate">{user?.email}</div>
                    <Button onClick={doLogout} variant="outline" className="mt-3 w-full rounded-sm border-slate-700 bg-slate-800 hover:bg-slate-700 text-white" data-testid="admin-logout-btn">
                        <LogOut className="w-4 h-4 mr-2" /> Logout
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-x-auto">
                <div className="p-8">
                    {loading && !stats ? (
                        <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>
                    ) : (
                        <>
                            {tab === "dashboard" && <Dashboard stats={stats} orders={orders.slice(0, 8)} pricing={pricing} saveRate={saveRate} />}
                            {tab === "orders" && (
                                <OrdersTable
                                    orders={orders}
                                    filter={filter}
                                    setFilter={setFilter}
                                    updateStatus={updateStatus}
                                />
                            )}
                            {tab === "customers" && <CustomersTable customers={customers} />}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

function NavBtn({ icon: Icon, label, active, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-heading font-bold uppercase tracking-wider transition-colors ${active ? "bg-blue-700 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}

function Dashboard({ stats, orders, pricing, saveRate }) {
    if (!stats) return null;
    const kpis = [
        { icon: ClipboardList, label: "Today's Orders", value: stats.today_orders, color: "text-blue-700" },
        { icon: TrendingUp, label: "Total Orders", value: stats.total_orders, color: "text-slate-900" },
        { icon: Droplets, label: "Litres Delivered", value: `${stats.litres_delivered}L`, color: "text-blue-600" },
        { icon: IndianRupee, label: "Revenue (Delivered)", value: `₹${Math.round(stats.revenue).toLocaleString("en-IN")}`, color: "text-emerald-600" },
    ];
    return (
        <div data-testid="admin-dashboard">
            <div className="mb-8">
                <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-2">// Control Room</div>
                <h1 className="font-heading font-black text-4xl tracking-tighter text-slate-900">Dashboard</h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {kpis.map(k => (
                    <div key={k.label} className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-plex">{k.label}</div>
                            <k.icon className={`w-4 h-4 ${k.color}`} />
                        </div>
                        <div className={`mt-4 font-heading font-black text-4xl tracking-tighter tab-num ${k.color}`}>{k.value}</div>
                    </div>
                ))}
            </div>

            {/* Daily Rate Management */}
            <div className="bg-slate-900 text-white p-6 md:p-8 rounded-sm mb-8 relative overflow-hidden" data-testid="admin-rate-widget">
                <div className="absolute inset-0 hero-grid opacity-20"></div>
                <RateEditor pricing={pricing} saveRate={saveRate} />
            </div>

            <div className="grid lg:grid-cols-5 gap-4">
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
                    <h3 className="font-heading font-bold text-slate-900 tracking-tight mb-4">Order Status Breakdown</h3>
                    <div className="space-y-3">
                        {STATUS_ORDER.map(s => {
                            const count = stats.by_status[s] || 0;
                            const c = STATUS_COLORS[s];
                            const pct = stats.total_orders ? (count / stats.total_orders) * 100 : 0;
                            return (
                                <div key={s}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="font-heading font-bold text-slate-700">{s}</span>
                                        <span className="font-mono-plex tab-num text-slate-600">{count}</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-100">
                                        <div className={`h-full ${c.bg.replace("100", "500")}`} style={{ width: `${pct}%` }}></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="lg:col-span-3 bg-white border border-slate-200 rounded-sm p-6">
                    <h3 className="font-heading font-bold text-slate-900 tracking-tight mb-4">Recent Orders</h3>
                    {orders.length === 0 ? (
                        <div className="text-sm text-slate-500">No orders yet.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono-plex border-b border-slate-200">
                                    <th className="pb-2">Order</th><th className="pb-2">Customer</th><th className="pb-2">Qty</th><th className="pb-2">Total</th><th className="pb-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(o => {
                                    const c = STATUS_COLORS[o.status];
                                    return (
                                        <tr key={o.id} className="border-b border-slate-100 last:border-0">
                                            <td className="py-2 font-mono-plex tab-num font-bold">{o.order_number}</td>
                                            <td className="py-2">{o.contact_name}</td>
                                            <td className="py-2 tab-num">{o.quantity_litres}L</td>
                                            <td className="py-2 tab-num font-bold">₹{o.total.toFixed(0)}</td>
                                            <td className="py-2"><span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${c.bg} ${c.text}`}>{o.status}</span></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function RateEditor({ pricing, saveRate }) {
    const [price, setPrice] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (pricing) {
            setPrice(String(pricing.price_per_litre ?? ""));
            setNote(pricing.rate_note ?? "");
        }
    }, [pricing]);

    const onSave = async () => {
        const p = parseFloat(price);
        if (Number.isNaN(p) || p < 50 || p > 500) {
            toast.error("Enter a valid rate between ₹50 and ₹500");
            return;
        }
        setSaving(true);
        await saveRate(p, note);
        setSaving(false);
    };

    return (
        <div className="relative grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-1">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                    <Gauge className="w-4 h-4" />
                    <div className="text-[11px] font-mono-plex uppercase tracking-[0.2em]">// Today's Rate</div>
                </div>
                <div className="font-heading font-black text-slate-100 text-3xl tracking-tighter leading-tight">Daily BPCL rate control.</div>
                <p className="mt-2 text-sm text-slate-400">
                    Update the per-litre rate every morning after the BPCL depot revision. Customers see the new rate live inside the order form.
                </p>
                {pricing?.updated_at && (
                    <div className="mt-4 text-[11px] font-mono-plex uppercase tracking-widest text-slate-500">
                        Last updated: {new Date(pricing.updated_at).toLocaleString("en-IN")}
                        {pricing.updated_by ? ` · by ${pricing.updated_by}` : ""}
                    </div>
                )}
            </div>
            <div className="md:col-span-2 grid gap-4">
                <div className="grid sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-1">
                        <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 block">Rate ₹ / litre</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="50"
                            max="500"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="rounded-sm bg-slate-800 border-slate-700 text-white font-mono-plex tab-num text-lg h-12"
                            data-testid="admin-rate-price-input"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 block">Rate note (optional, shown to customers on order form)</Label>
                        <Textarea
                            rows={2}
                            maxLength={280}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g., Rate revised at 6:30 AM based on BPCL depot"
                            className="rounded-sm bg-slate-800 border-slate-700 text-white resize-none"
                            data-testid="admin-rate-note-input"
                        />
                    </div>
                </div>
                <Button
                    onClick={onSave}
                    disabled={saving}
                    className="rounded-sm bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold uppercase tracking-wider h-11 w-full sm:w-auto sm:justify-self-end sm:px-8"
                    data-testid="admin-rate-save-btn"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Rate</>}
                </Button>
            </div>
        </div>
    );
}

function OrdersTable({ orders, filter, setFilter, updateStatus }) {
    return (
        <div data-testid="admin-orders">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-2">// Orders</div>
                    <h1 className="font-heading font-black text-4xl tracking-tighter text-slate-900">Orders Management</h1>
                </div>
                <div className="w-56">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="rounded-sm bg-white" data-testid="admin-orders-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="All">All Statuses</SelectItem>
                            {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <Th>Order #</Th><Th>Date</Th><Th>Customer</Th><Th>Phone</Th><Th>Qty</Th><Th>Total</Th><Th>Slot</Th><Th>Address</Th><Th>Status</Th><Th>Update</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 && (
                            <tr><td colSpan={10} className="p-10 text-center text-slate-500">No orders match this filter.</td></tr>
                        )}
                        {orders.map((o, i) => {
                            const c = STATUS_COLORS[o.status];
                            return (
                                <tr key={o.id} className={i % 2 === 1 ? "bg-slate-50" : ""} data-testid={`admin-order-row-${o.order_number}`}>
                                    <Td><span className="font-mono-plex tab-num font-bold text-slate-900">{o.order_number}</span></Td>
                                    <Td>{new Date(o.created_at).toLocaleDateString("en-IN")}</Td>
                                    <Td>
                                        <div className="font-heading font-bold text-slate-900">{o.contact_name}</div>
                                        {o.company_name && <div className="text-[11px] text-slate-500">{o.company_name}</div>}
                                    </Td>
                                    <Td className="tab-num">{o.contact_phone}</Td>
                                    <Td className="tab-num">{o.quantity_litres}L</Td>
                                    <Td className="tab-num font-bold">₹{o.total.toFixed(0)}</Td>
                                    <Td className="max-w-[10rem]">
                                        <div>{o.preferred_date}</div>
                                        <div className="text-[11px] text-slate-500">{o.preferred_slot}</div>
                                    </Td>
                                    <Td className="max-w-[14rem]"><div className="truncate">{o.delivery_address}</div><div className="text-[11px] text-slate-500">{o.city} · {o.pincode}</div></Td>
                                    <Td><span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${c.bg} ${c.text}`}>{o.status}</span></Td>
                                    <Td>
                                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                                            <SelectTrigger className="rounded-sm h-8 text-xs w-40" data-testid={`admin-status-select-${o.order_number}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CustomersTable({ customers }) {
    return (
        <div data-testid="admin-customers">
            <div className="mb-6">
                <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-2">// Customers</div>
                <h1 className="font-heading font-black text-4xl tracking-tighter text-slate-900">Registered Customers</h1>
                <p className="text-slate-600 mt-1 text-sm">{customers.length} total customers</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Type</Th><Th>Company</Th><Th>GST</Th><Th>Joined</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.length === 0 && (
                            <tr><td colSpan={7} className="p-10 text-center text-slate-500">No customers yet.</td></tr>
                        )}
                        {customers.map((c, i) => (
                            <tr key={c.id} className={i % 2 === 1 ? "bg-slate-50" : ""} data-testid={`customer-row-${c.email}`}>
                                <Td className="font-heading font-bold text-slate-900">{c.name}</Td>
                                <Td>{c.email}</Td>
                                <Td className="tab-num">{c.phone}</Td>
                                <Td>{c.customer_type}</Td>
                                <Td>{c.company_name || "—"}</Td>
                                <Td className="tab-num">{c.gst_number || "—"}</Td>
                                <Td>{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "—"}</Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-slate-600 font-mono-plex">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-slate-700 align-top ${className}`}>{children}</td>;
